import { METRIC } from "@/constants/testIds";

const TEAL = "#B8D4D0";
const SAGE = "#9AADA8";
const REF = "#d8b878"; // reference marker (first week 2026)

/**
 * Horizontal 52-week percentile scale.
 *
 *   52-WEEK LOOKBACK                                                   CURRENT WEEK
 *
 *                          ● first week 2026
 *                          +12.30%  2026-01-06
 *  ──────●──────────────────●──────────────────●─────  baseline ─────
 *        ●5th               ●50th              ●95th
 *        labels strictly BELOW the line so the line never crosses text
 *                                                          │
 *                                                          ●current
 *                                                          2nd Pctile
 *                                                          -1.89%
 */
export default function PercentileScale({ metricId, scale, current }) {
    if (
        scale == null ||
        scale.min == null ||
        scale.max == null ||
        current == null ||
        current.pct_rank_52w == null
    ) {
        return null;
    }

    const currentPct = clamp(current.pct_rank_52w, 0, 100);

    const lowerMarkers = [
        { key: "p5", label: "5th", pct: 5, value: scale.p5, color: SAGE },
        { key: "1sd_lo", label: "16th (1SD)", pct: 16, value: scale.p16, color: SAGE },
        { key: "p50", label: "50th", pct: 50, value: scale.p50, color: SAGE },
        { key: "1sd_hi", label: "84th (1SD)", pct: 84, value: scale.p84, color: SAGE },
        { key: "p95", label: "95th", pct: 95, value: scale.p95, color: SAGE },
    ];

    const LINE_TOP = 60;

    return (
        <div data-testid={METRIC.percentileScale(metricId)}>
            <div className="flex items-center justify-between mb-2">
                <span className="pord-label">52-week lookback</span>
                <span className="pord-label" style={{ color: TEAL }}>
                    current week
                </span>
            </div>

            <div className="relative h-[180px] mt-2 mx-2">
                {/* horizontal baseline */}
                <div
                    className="absolute left-0 right-0 h-px"
                    style={{
                        top: LINE_TOP,
                        background:
                            "linear-gradient(to right, rgba(154,173,168,0.15), rgba(184,212,208,0.55), rgba(154,173,168,0.15))",
                    }}
                />

                {/* p5 / p50 / p95 — below the line */}
                {lowerMarkers.map((m) => (
                    <DownMarker
                        key={m.key}
                        left={m.pct}
                        baseTop={LINE_TOP}
                        label={m.label}
                        sub={fmtPct(m.value)}
                        color={m.color}
                        size={9}
                    />
                ))}

                {/* connector from line down to current marker */}
                <div
                    className="absolute"
                    style={{
                        left: `${currentPct}%`,
                        top: LINE_TOP,
                        height: 70,
                        width: 1,
                        background:
                            "linear-gradient(to bottom, rgba(184,212,208,0.55), rgba(184,212,208,0.0))",
                        transform: "translateX(-0.5px)",
                    }}
                />

                {/* Current — dedicated lower row */}
                <CurrentMarker
                    testId={METRIC.percentileMarker(metricId)}
                    left={currentPct}
                    top={LINE_TOP + 70}
                    label={`${ordinal(currentPct)} Pctile`}
                    sub={fmtPct(current.net_pct_oi)}
                    color={TEAL}
                />
            </div>
        </div>
    );
}

/**
 * Below-the-line marker.
 *
 * Layout column (top -> bottom), top of box sits ON the line:
 *   [ circle, vertically shifted up by half its size so its CENTER lies on the line ]
 *   [ label  ]
 *   [ value  ]
 *
 * This guarantees the horizontal line never crosses any text.
 */
function DownMarker({ left, baseTop, label, sub, color, size = 9, testId }) {
    return (
        <div
            data-testid={testId}
            className="absolute flex flex-col items-center text-center pointer-events-none"
            style={{
                left: `${left}%`,
                top: baseTop,
                transform: "translateX(-50%)",
                width: 90,
            }}
        >
            <div
                className="rounded-full"
                style={{
                    width: size,
                    height: size,
                    background: "transparent",
                    border: `1.5px solid ${color}`,
                    marginTop: -Math.round(size / 2),
                }}
            />
            <div
                className="mt-3 pord-label whitespace-nowrap"
                style={{ color, letterSpacing: "0.12em" }}
            >
                {label}
            </div>
            <div className="mt-0.5 text-[11px] font-light tabular-nums whitespace-nowrap text-white/70">
                {sub}
            </div>
        </div>
    );
}

/**
 * Above-the-line marker. Box bottom sits ON the line.
 */
function UpMarker({ left, baseTop, label, sub, meta, color }) {
    const size = 9;
    return (
        <div
            className="absolute flex flex-col items-center text-center pointer-events-none"
            style={{
                left: `${left}%`,
                top: baseTop,
                transform: "translate(-50%, -100%)",
                width: 140,
            }}
        >
            <div className="text-[11px] font-light tabular-nums" style={{ color }}>
                {sub}
            </div>
            <div
                className="pord-label mt-0.5"
                style={{ color, letterSpacing: "0.12em" }}
            >
                {label}
            </div>
            {meta && (
                <div className="text-[10px] font-light text-white/40 mt-0.5">
                    {meta}
                </div>
            )}
            <div
                className="mt-1.5 rounded-full"
                style={{
                    width: size,
                    height: size,
                    background: "transparent",
                    border: `1.5px solid ${color}`,
                    marginBottom: -Math.round(size / 2),
                }}
            />
        </div>
    );
}

function CurrentMarker({ left, top, label, sub, color, testId }) {
    const size = 14;
    return (
        <div
            data-testid={testId}
            className="absolute flex flex-col items-center text-center pointer-events-none"
            style={{
                left: `${left}%`,
                top,
                transform: "translate(-50%, -50%)",
                width: 160,
            }}
        >
            <div
                className="rounded-full"
                style={{
                    width: size,
                    height: size,
                    background: color,
                    border: `1.5px solid ${color}`,
                    boxShadow: `0 0 0 5px rgba(184,212,208,0.15)`,
                }}
            />
            <div
                className="mt-2 pord-label whitespace-nowrap"
                style={{ color, letterSpacing: "0.12em" }}
            >
                {label}
            </div>
            <div className="mt-0.5 text-[12px] font-light tabular-nums text-white whitespace-nowrap">
                {sub}
            </div>
        </div>
    );
}

function clamp(v, lo, hi) {
    return Math.min(hi, Math.max(lo, v));
}

function ordinal(v) {
    const r = Math.round(v);
    const m100 = r % 100;
    const m10 = r % 10;
    if (m100 >= 11 && m100 <= 13) return `${r}th`;
    if (m10 === 1) return `${r}st`;
    if (m10 === 2) return `${r}nd`;
    if (m10 === 3) return `${r}rd`;
    return `${r}th`;
}

function fmtPct(v) {
    if (v == null) return "\u2014";
    const sign = v > 0 ? "+" : "";
    return `${sign}${Number(v).toFixed(2)}%`;
}
